/**
 * @name MenuUser
 * @description Menú de acciones de usuario (Settings, Onboarding, Logout, Language)
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import styled from 'styled-components';

import { Environment } from '@config';
import { AuthService, EmployeeService, ResponseHandlerService, SnackbarService, PreviousRouteService } from '@services';
import { Avatar, Icons, MUI, Typography } from '@styles';

class MenuUser extends Component {
  constructor(props) {
    super(props);
    this.enterpriseParams = JSON.parse(localStorage.getItem('ls.infoUsuario'))?.Empresa;
    this.state = {
      anchorEl: null,
      anchorElLang: null,
      user: { name: null, job: null, img: null },
      isExternal: props.isExternal,
      history: props.history
    };
  }

  componentDidMount() {
    const { isExternal, user } = this.state;
    const employee = JSON.parse(localStorage.getItem('ls.infoUsuario'))?.Empleado;

    if (isExternal) {
      // Usuario externo
      user.name = employee.Nombre;
      user.job = i18n.t('GenericMessages.userExternal');
      user.img = employee.Foto || 'https://assets.talentry.io/icons/profile/TAL_Avatar.png';
    } else {
      // Usuario interno
      user.name = `${employee.Nombres} ${employee.Apellidos}`;
      user.job = employee.Puesto;
      user.img = employee.Foto.length > 0 ? employee.Foto : 'https://assets.talentry.io/icons/profile/TAL_Avatar.png';
    }

    this.setState({ user });
  }

  openHandler = (event) => {
    this.setState({
      anchorEl: event.currentTarget,
      anchorElLang: null
    });
  }

  closeHandler = () => {
    this.setState({
      anchorEl: null,
      anchorElLang: null
    });
  }

  openHandlerLang = (event) => {
    this.setState({ anchorElLang: event.currentTarget });
  }

  closeHandlerLang = () => {
    this.setState({ anchorElLang: null });
  }

  goToSettings = () => {
    let URL = Environment.getCurrent().domain;
    window.location.href = `${URL.substring(0, URL.length - 2)}talentry/#/configuraperfil`;
    //PreviousRouteService.setPreviousRoute(`talentry/#/configuraperfil`, `/home`);
  }

  logout = () => {
    //PreviousRouteService.cleanPreviousRoute();
    AuthService.clearStorage();
    AuthService.logout();
  }

  /**
   * Cambiar el lenguaje de la plataforma
   * @param {string} lang (es, en, pt)
   */
  changeLanguage = (lang) => {
    EmployeeService.putProfile({ language: lang })
      .then(() => {
        // Switch Language
        localStorage.setItem('ls.ty-Language', JSON.stringify({ language: lang }));
        i18n.use(initReactI18next).init({ lng: lang });

        EmployeeService.getInfo()
          .then((response) => {
            localStorage.removeItem('ls.infoUsuario');
            localStorage.setItem('ls.infoUsuario', JSON.stringify(response));
          });

        SnackbarService.success(i18n.t('GenericMessages.languageUpdated'));
        this.setState({ anchorElLang: null });
        window.location.reload();
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }

  render() {
    const {
      user,
      anchorEl,
      anchorElLang,
      isExternal,
      history
    } = this.state;

    return (
      <>
        <Styled.BoxTopbarIcon onClick={this.openHandler}>
          <MUI.Tooltip title={i18n.t('Topbar.userMenu')}>
            <Styled.BoxAvatar>
              <Avatar
                size="xs"
                src={user.img}
              />
              <Icons.ArrowDown />
            </Styled.BoxAvatar>
          </MUI.Tooltip>
        </Styled.BoxTopbarIcon>

        <Styled.Popover
          open={Boolean(anchorEl)}
          onClose={this.closeHandler}
          anchorEl={anchorEl}
        >
          <MUI.MenuHeader>
            <Styled.BoxHeader>
              <Typography.Small>{user.name}</Typography.Small>
              <Typography.Small color="greyDarkAlpha">{user.job}</Typography.Small>
            </Styled.BoxHeader>
          </MUI.MenuHeader>

          <Styled.BoxUserActions>
            {/* Configuración */}
            {/* NOTA: Ocultar opción para ID 93 KMPG y ID 1126 KPMG Costa Rica */}
            {(!isExternal && this.enterpriseParams?.IdEmpresa !== 93 && this.enterpriseParams?.IdEmpresa !== 1126) && (
              <MUI.MenuItem onClick={this.goToSettings}>
                <Styled.BoxIcon>
                  <Icons.Settings />
                </Styled.BoxIcon>
                <Typography.Text>{i18n.t('Topbar.setting')}</Typography.Text>
              </MUI.MenuItem>
            )}

            {/* Onboarding */}
            {/* !isExternal && ( 
              <MenuItem>
                  {i18n.t('Topbar.onboarding')}
              </MenuItem>
            )*/}

            {/* Logout */}
            <MUI.MenuItem onClick={() => this.logout(history)}>
              <Styled.BoxIcon>
                <Icons.ExitToApp />
              </Styled.BoxIcon>
              <Typography.Text>{i18n.t('Topbar.logout')}</Typography.Text>
            </MUI.MenuItem>

            {/* Idioma */}
            <MUI.MenuItem onClick={this.openHandlerLang}>
              <Styled.BoxIcon>
                <Icons.Translate />
              </Styled.BoxIcon>
              <Typography.Text>{i18n.t('Topbar.language')}</Typography.Text>
              <MUI.Box marginLeft="8px">
                <Styled.BoxIcon>
                  <Icons.ArrowDown />
                </Styled.BoxIcon>
              </MUI.Box>
            </MUI.MenuItem>
          </Styled.BoxUserActions>
        </Styled.Popover>

        <Styled.PopoverLang
          open={Boolean(anchorElLang)}
          onClose={this.closeHandlerLang}
          anchorEl={anchorElLang}
          isExternal={isExternal}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MUI.MenuItem onClick={() => this.changeLanguage('es')}>
            <Typography.Text>{i18n.t('GenericMessages.es')}</Typography.Text>
          </MUI.MenuItem>
          <MUI.MenuItem onClick={() => this.changeLanguage('en')}>
            <Typography.Text>{i18n.t('GenericMessages.en')}</Typography.Text>
          </MUI.MenuItem>
          <MUI.MenuItem onClick={() => this.changeLanguage('pt')}>
            <Typography.Text>{i18n.t('GenericMessages.pt')}</Typography.Text>
          </MUI.MenuItem>
        </Styled.PopoverLang>
      </>
    );
  }
}

MenuUser.propTypes = {
  // Boolean que indica si es usuario externo.
  isExternal: PropTypes.bool.isRequired,
  // History router.
  history: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxTopbarIcon = styled(MUI.ButtonText)`
  &.MuiButton-root{
    display: flex;
    width: auto;
    height: 60px;
    padding: 8px 12px;
    margin: 0;

    &:hover {
      text-decoration: none;
      background-color: ${(props) => props.theme.colors.greyHover};
      border-radius: 0%;
    }
  }
`;

Styled.BoxAvatar = styled.div`
  display: flex;
  align-items: center;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

Styled.BoxHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

Styled.BoxUserActions = styled.div`
  max-height: 470px;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.white};
  backface-visibility: hidden;
  scrollbar-width: thin !important; /* Scrollbar Firefox */
`;

Styled.BoxIcon = styled.div`
  display: flex;
  margin-right: 12px;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

// *************************************
// GLOBAL STYLED COMPONENTS          
// *************************************

Styled.Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    min-width: 280px;
    max-height: 520px;
    max-width: 608px;
    left: auto !important;
    right: 16px !important;
    top: 60px !important;
    overflow-y: hidden;
    overflow-x: hidden;
  }
`;

Styled.PopoverLang = styled(MUI.Popover)`
  .MuiPopover-paper {
    max-height: 520px;
    max-width: 608px;
    min-width: 220px;

    // left: auto !important;
    // right: 16px !important;
    // top: ${(props) => !props.isExternal ? 270 : 220}px !important;
  }
`;

export default MenuUser;